import { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import LoginVideo from '../assets/Login_Video.webp';
import { login as apiLogin } from '../services/authService';

const Login = ({ onLogin }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const from = location.state?.from?.pathname || '/dashboard';
    const [formData, setFormData] = useState({
        email: '',
        password: '',
    });
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState({});

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.email) {
            newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Please enter a valid email address';
        }

        if (!formData.password) {
            newErrors.password = 'Password is required';
        } else if (formData.password.length < 6) {
            newErrors.password = 'Password must be at least 6 characters';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        setIsLoading(true);

    try {
            await new Promise(resolve => setTimeout(resolve, 500));

            const user = await apiLogin(formData.email.trim(), formData.password);

            if (onLogin) {
                onLogin(user);
                navigate(from, { replace: true });
            }
        } catch (error) {
            setErrors({ general: error.message || 'Invalid email or password. Please check your credentials.' });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            {/* Mobile restriction message - shown only on screens smaller than tablet (< 768px) */}
            <div className="md:hidden min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-purple-900 flex items-center justify-center p-6">
                <div className="text-center text-white max-w-md">
                    <div className="w-20 h-20 mx-auto mb-6 bg-white/10 rounded-full flex items-center justify-center backdrop-blur-sm">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="size-9">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
                        </svg>

                    </div>
                    <h1 className="text-2xl font-bold mb-4">Mobile Not Supported</h1>
                    <p className="text-blue-200 mb-6 leading-relaxed">
                        This application is optimized for tablet and desktop use. Please access this tool from a tablet, desktop, or laptop computer for the best experience.
                    </p>
                </div>
            </div>

            {/* Tablet & Desktop view - login page */}
            <div className="hidden md:flex min-h-screen">
                <div className="flex-[2] relative overflow-hidden hidden lg:block">
                    <img
                        src={LoginVideo}
                        alt="Login background"
                        className="absolute inset-0 w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/20"></div>
                </div>

                <div className="flex-1 lg:flex-[1] bg-gray-50 flex items-center justify-center p-8">
                    <div className="w-full max-w-md">
                        <div className="flex justify-center items-center space-x-3 mb-6">
                            <img 
                                src="/Original.svg" 
                                alt="Actelligence Logo" 
                                className="h-9 w-auto"
                            />
                            <div className="text-center">
                                <h1 className="text-4xl font-bold mb-1 bg-gradient-to-r from-[#355699] via-[#307a78] to-[#86be22] bg-clip-text text-transparent h-11">ACT<span className='italic'>elligence</span></h1>
                            </div>
                        </div>

                        <div className="bg-white py-8 px-6 border-[rgb(42,26,31)] rounded-[10px] shadow-card opacity-90 sm:px-10">
                            <h2 className='text-xl font-bold flex justify-center mb-5 text-gray-900'>Login to Access Your Account</h2>
                            <form className="space-y-6" onSubmit={handleSubmit}>
                                <div>
                                    <label htmlFor="email" className="label">
                                        Email address*
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        </div>
                                        <input
                                            id="email"
                                            name="email"
                                            type="email"
                                            autoComplete="email"
                                            required
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            className={`input pl-10 ${errors.email ? 'border-red-500 focus:ring-red-500' : ''}`}
                                            placeholder="Enter your email"
                                        />
                                    </div>
                                    {errors.email && (
                                        <p className="mt-2 text-sm text-red-600">{errors.email}</p>
                                    )}
                                </div>

                                <div>
                                    <label htmlFor="password" className="label">
                                        Password*
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        </div>
                                        <input
                                            id="password"
                                            name="password"
                                            type={showPassword ? 'text' : 'password'}
                                            autoComplete="current-password"
                                            required
                                            value={formData.password}
                                            onChange={handleInputChange}
                                            className={`input pl-10 pr-10 ${errors.password ? 'border-red-500 focus:ring-red-500' : ''}`}
                                            placeholder="Enter your password"
                                        />
                                        <button
                                            type="button"
                                            className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                            onClick={() => setShowPassword(!showPassword)}
                                        >
                                            {showPassword ? (
                                                <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                                            ) : (
                                                <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                                            )}
                                        </button>
                                    </div>
                                    {errors.password && (
                                        <p className="mt-2 text-sm text-red-600">{errors.password}</p>
                                    )}
                                </div>



                                {errors.general && (
                                    <div className="bg-red-50 border border-red-200 rounded-md p-3">
                                        <p className="text-sm text-red-600">{errors.general}</p>
                                    </div>
                                )}

                                <div>
                                    <button
                                        type="submit"
                                        disabled={isLoading}
                                        className={`w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white transition-all duration-200 ${
                                            isLoading
                                            ? 'bg-gray-400 cursor-not-allowed'
                                            : 'bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500'
                                            }`}
                                    >
                                        {isLoading ? (
                                            <div className="flex items-center space-x-2">
                                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                <span>Signing in...</span>
                                            </div>
                                        ) : (
                                            'Sign in'
                                        )}
                                    </button>
                                </div>

                                {/* Forgot Password link */}
                                <div className="text-center">
                                  <Link
                                    to="/forgot-password"
                                    className="text-sm text-emerald-600 hover:text-emerald-700 hover:underline transition-colors"
                                  >
                                    Forgot your password?
                                  </Link>
                                </div>

                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Login;